// Camera rig with two modes:
//   ORBIT (default) — left-drag rotate, right/middle-drag pan, wheel zoom, WASD pan.
//   FLY  (press F)  — pointer-lock mouse-look + WASD/QE to walk the level. Esc exits.

import * as THREE from "three";
import { CAMERA } from "../config";
import type { Input } from "../engine/input";

const UP = new THREE.Vector3(0, 1, 0);

export class CameraRig {
  mode: "orbit" | "fly" = "orbit";

  // orbit state (spherical around a target)
  private target = new THREE.Vector3(0, 100, 0);
  private radius = 3000;
  private theta = 0.6; // azimuth
  private phi = 0.85; // polar from +Y
  private introMul = 1; // >1 = camera starts pulled back/high, decays to 1 (the arrival "settle onto the field")

  // fly state
  private pos = new THREE.Vector3();
  private yaw = 0;
  private pitch = 0;

  // per-frame scratch (avoid re-allocating Vector3s each camera update). Never retained across calls.
  private _off = new THREE.Vector3();
  private _fwd = new THREE.Vector3();
  private _right = new THREE.Vector3();
  private _lookTarget = new THREE.Vector3();

  constructor(
    private cam: THREE.PerspectiveCamera,
    private input: Input,
  ) {}

  /** Kick off the "arrival" — the camera starts pulled back + high and eases down onto the framed level,
   *  synced with the landing wash fading out. Cancels itself once settled (or when the user takes control). */
  arrive(): void {
    this.introMul = 2.4;
  }

  /** Recenter the orbit camera to frame a level's bounding box. */
  frameLevel(bbox: { minX: number; maxX: number; minZ: number; maxZ: number }): void {
    const cx = (bbox.minX + bbox.maxX) / 2;
    const cz = (bbox.minZ + bbox.maxZ) / 2;
    this.target.set(cx, 120, cz);
    const span = Math.max(bbox.maxX - bbox.minX, bbox.maxZ - bbox.minZ);
    this.radius = span * 1.15 + 550;
    this.theta = 0.7;
    this.phi = 0.62;
    if (this.mode === "fly") this.input.exitFly();
    this.mode = "orbit";
    this.applyOrbit();
  }

  private flyForward(): THREE.Vector3 {
    const cp = Math.cos(this.pitch);
    return this._fwd.set(
      Math.sin(this.yaw) * cp,
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * cp,
    );
  }

  private enterFly(): void {
    this.pos.copy(this.cam.position);
    const dir = new THREE.Vector3();
    this.cam.getWorldDirection(dir);
    this.yaw = Math.atan2(dir.x, -dir.z);
    this.pitch = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1));
    this.mode = "fly";
    this.input.requestFly();
  }

  private exitToOrbit(): void {
    const fwd = this.flyForward();
    this.target.copy(this.pos).addScaledVector(fwd, 1000);
    const off = new THREE.Vector3().subVectors(this.pos, this.target);
    this.radius = Math.max(off.length(), 600);
    this.phi = Math.acos(THREE.MathUtils.clamp(off.y / this.radius, -1, 1));
    this.theta = Math.atan2(off.x, off.z);
    this.mode = "orbit";
  }

  update(dt: number): void {
    if (this.input.consumeKeyPress("KeyF") && this.mode === "orbit") this.enterFly();
    if (this.mode === "fly" && !this.input.locked) this.exitToOrbit();

    if (this.mode === "fly") this.updateFly(dt);
    else this.updateOrbit(dt);
  }

  private updateFly(dt: number): void {
    const look = this.input.consumeLook();
    // yaw uses += (fly's flyForward() parameterizes forward.x as +sin(yaw), the
    // opposite of the unit player's -sin(yaw)); with -= the horizontal look came
    // out inverted vs unit mode. pitch stays -= (non-inverted: mouse-down looks down).
    this.yaw += look.dx * CAMERA.lookSpeed;
    this.pitch -= look.dy * CAMERA.lookSpeed;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.5, 1.5);

    const fwd = this.flyForward(); // = this._fwd
    const right = this._right.crossVectors(fwd, UP).normalize();
    const speed = CAMERA.flySpeed * (this.input.isBoost() ? CAMERA.flyBoost : 1) * dt;
    const mv = this.input.moveAxis();
    this.pos.addScaledVector(fwd, -mv.z * speed);
    this.pos.addScaledVector(right, mv.x * speed);
    this.pos.y += this.input.vertical() * speed;

    this.cam.position.copy(this.pos);
    this.cam.lookAt(this._lookTarget.copy(this.pos).add(fwd)); // _lookTarget distinct from _fwd
  }

  private updateOrbit(dt: number): void {
    const drag = this.input.consumeDrag();
    if (drag.button === 0 || drag.button === 2) {
      // left OR RIGHT drag rotates the camera (right-drag orbit, like most builders/RTS). In build mode
      // left-click places, so right-drag is the rotate you actually use there.
      this.theta -= drag.dx * 0.008;
      this.phi -= drag.dy * 0.008;
      this.phi = THREE.MathUtils.clamp(this.phi, 0.05, Math.PI * 0.492);
      if (drag.dx || drag.dy) this.introMul = 1; // the player grabbed the camera → drop the arrival move
    } else if (drag.button === 1) {
      this.panGround(-drag.dx, drag.dy, this.radius * 0.0016); // middle drag pans (WASD also pans)
    }

    const w = this.input.consumeWheel();
    if (w) { this.radius = THREE.MathUtils.clamp(this.radius * (1 + w * 0.12), 200, 30000); this.introMul = 1; }

    // ease the arrival "settle" toward 1 (≈2.5 s to land)
    if (this.introMul > 1.0005) this.introMul = 1 + (this.introMul - 1) * Math.exp(-dt / 0.75);
    else this.introMul = 1;

    // WASD pans the orbit target across the ground plane. mv.x maps straight to
    // ground-right (D → screen-right); the earlier -mv.x inverted A/D vs W/S.
    const mv = this.input.moveAxis();
    if (mv.x !== 0 || mv.z !== 0) {
      const sp = CAMERA.panSpeed * (this.input.isBoost() ? CAMERA.panBoost : 1); // Shift = fast pan
      this.panGround(mv.x * sp, mv.z * sp, this.radius * 0.0016);
    }

    this.applyOrbit();
  }

  /** Pan the target along the ground using screen-right / screen-forward derived from azimuth. */
  private panGround(dx: number, dy: number, scale: number): void {
    const gf = new THREE.Vector3(Math.sin(this.theta), 0, Math.cos(this.theta)); // target -> camera (horizontal)
    const gr = new THREE.Vector3(gf.z, 0, -gf.x);
    this.target.addScaledVector(gr, dx * scale);
    this.target.addScaledVector(gf, dy * scale);
  }

  private applyOrbit(): void {
    const sinP = Math.sin(this.phi);
    const off = this._off.set(
      sinP * Math.sin(this.theta),
      Math.cos(this.phi),
      sinP * Math.cos(this.theta),
    ).multiplyScalar(this.radius * this.introMul);
    this.cam.position.copy(this.target).add(off);
    this.cam.lookAt(this.target);
  }

  /** Fill `out` with the current focus point (fly eye or orbit target). Out-param avoids a per-frame alloc;
   *  the caller must read the fields synchronously and not retain the reference. */
  focusPoint(out: { x: number; z: number }): { x: number; z: number } {
    const p = this.mode === "fly" ? this.pos : this.target;
    out.x = p.x; out.z = p.z;
    return out;
  }
}
